import { Button, Card, ContentLayout, TemplateWrapper } from "@2u-landing-pages/components";
import { Container } from "@red-digital/bricks";
import cn from "classnames";
import React, { useEffect, useMemo, useRef, useState } from "react";
import type { ResponsiveObject, Settings } from "react-slick";
import Slider from "react-slick";
import { CarouselProps } from "../../Carousel.types";
import { Pagination } from "../../components/Pagination";

import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import styles from "./Default.module.scss";

interface SettingsExtended extends Omit<Settings, "responsive"> {
  responsive: [ResponsiveObject];
}

const slide = (sliderRef: React.MutableRefObject<Slider | null>, direction: "next" | "prev") => {
  if (!sliderRef.current) return;
  if (direction === "next") {
    sliderRef.current.slickNext();
  } else {
    sliderRef.current.slickPrev();
  }
};

export const Default: React.FC<CarouselProps> = ({
  section_heading,
  items,
  section_background_color,
  button_color,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.matchMedia(`(max-width: 1023px)`).matches : false
  );
  const sliderRef = useRef<Slider | null>(null);
  const totalSlides = items?.length || 0;
  const carouselLayout = items?.[0]?.fields?.variation || "large";

  const settings: SettingsExtended = useMemo(() => {
    let baseSettings: SettingsExtended = {
      dots: false,
      prevArrow: <></>,
      nextArrow: <></>,
      infinite: true,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      initialSlide: 0,
      draggable: true,
      responsive: [
        {
          breakpoint: 1024,
          settings: {
            infinite: true,
            draggable: true,
          },
        },
      ],
    };

    if (totalSlides <= 2) {
      (baseSettings.responsive[0].settings as Settings).infinite = false;
    }

    if (totalSlides <= 4 && carouselLayout === "small") {
      baseSettings.infinite = false;
      baseSettings.slidesToShow = totalSlides;
      baseSettings.draggable = false;
      (baseSettings.responsive[0].settings as Settings).slidesToShow = 1;
    }

    if (totalSlides === 4 && carouselLayout === "small") {
      baseSettings.responsive[0].breakpoint = 1160;
    }

    baseSettings.beforeChange = (_: number, newIndex: number) => {
      setActiveIndex(newIndex);
    };

    return baseSettings;
  }, [totalSlides, carouselLayout]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mediaQuery = window.matchMedia("(max-width: 1023px)");
    const handleScreenChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mediaQuery.addEventListener("change", handleScreenChange);

    return () => {
      mediaQuery.removeEventListener("change", handleScreenChange);
    };
  }, []);

  useEffect(() => {
    if (!isMobile && sliderRef.current) {
      sliderRef.current.slickGoTo(0);
    }
  }, [isMobile]);

  return (
    <TemplateWrapper padded backgroundColor={section_background_color}>
      <Container className={styles.carousel}>
        <ContentLayout
          className={styles.sectionHeader}
          content={section_heading}
          heading={{
            className: styles.heading,
            variant: "heading-2xl",
          }}
        />
        <Container flex justifyContent="center" className={styles.carouselWrapper}>
          {/* Possibly set up max width based on card and size */}
          <Slider
            ref={sliderRef}
            {...settings}
            className={cn(styles.slider, {
              [styles[`slider-${carouselLayout}`]]: !(totalSlides <= 4 && carouselLayout === "small"),
            })}
          >
            {items?.map((item) => {
              if (!item) return null;
              return <Card key={item.sys.id} className={styles.card} entry={item} />;
            })}
          </Slider>
        </Container>
        <div
          className={cn(styles[`controls-${settings.responsive[0].breakpoint}`], {
            [styles.controlsEnabled]:
              (totalSlides > 4 && carouselLayout === "small") ||
              (totalSlides > 1 && carouselLayout === "medium") ||
              (totalSlides > 1 && carouselLayout === "large"),
          })}
        >
          <Container flex alignItems="center" justifyContent="center">
            <Button
              variant="filled"
              icon="chevron-left"
              size="small"
              color={button_color || "primary"}
              onClick={() => slide(sliderRef, "prev")}
            />
            <Pagination activeIndex={activeIndex} totalSlides={totalSlides} sliderRef={sliderRef} />
            <Button
              variant="filled"
              icon="chevron-right"
              size="small"
              color={button_color || "primary"}
              onClick={() => slide(sliderRef, "next")}
            />
          </Container>
        </div>
      </Container>
    </TemplateWrapper>
  );
};
